import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  VoiceScope,
  VoiceContext,
  VoiceRecording,
  uploadRecording,
  getRecordings,
  retryTranscription,
  deleteRecording,
  updateTranscript,
  getAudioUrl,
  classifyRecording,
  ClassifyResult,
} from '../api/voice';
import { getStoredUser } from '../api/auth';
import './VoiceRecorder.css';

// MediaRecorder webm/ogg blobs ship without a duration header, so a fresh
// <audio> reports duration as Infinity and shows "0:00 / 0:00". Forcing a seek
// to the end makes the browser scan the file and compute the real duration.
const fixAudioDuration = (e: React.SyntheticEvent<HTMLAudioElement>) => {
  const el = e.currentTarget;
  if (el.duration === Infinity || Number.isNaN(el.duration)) {
    el.currentTime = 1e101;
    el.ontimeupdate = () => {
      el.ontimeupdate = null;
      el.currentTime = 0;
    };
  }
};

interface Props {
  scope: VoiceScope;
  context: VoiceContext;
  title?: string;
  subtitle?: string;
  // Fired after a note finishes transcribing, so a sibling (e.g. the auto-filled
  // comment field) can re-fetch.
  onTranscribed?: () => void;
}

// Pick the best container the browser supports for MediaRecorder.
const pickMimeType = (): string => {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  const MR = (window as any).MediaRecorder;
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const type of candidates) {
      if (MR.isTypeSupported(type)) return type;
    }
  }
  return '';
};

const fmtTime = (ms: number): string => {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const fmtWhen = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return iso;
  }
};

const statusLabel: Record<string, string> = {
  transcribing: 'Transcribing…',
  transcribed: 'Transcribed',
  empty: 'No speech detected',
  failed: 'Transcription failed',
  awaiting_key: 'Saved — add API key to transcribe',
};

const VoiceRecorder: React.FC<Props> = ({ scope, context, title, subtitle, onTranscribed }) => {
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<VoiceRecording[]>([]);
  const [classified, setClassified] = useState<Record<string, ClassifyResult>>({});
  const [classifyingId, setClassifyingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  // Which items have their raw transcript expanded (only relevant once a note has
  // been sorted into records, which otherwise duplicates the transcript text).
  const [showRaw, setShowRaw] = useState<Record<string, boolean>>({});

  const currentUserId = getStoredUser()?.id || null;

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0); // start of the current (unpaused) segment
  const accumulatedRef = useRef<number>(0); // ms banked from prior segments
  const timerRef = useRef<number | null>(null);

  // Total recorded time so far, accounting for paused gaps.
  const elapsedNow = () =>
    accumulatedRef.current + (startedAtRef.current ? Date.now() - startedAtRef.current : 0);

  // Re-fetch whenever the scope context changes (e.g. navigating between races).
  const contextKey = JSON.stringify({ scope, context });

  const loadItems = useCallback(async () => {
    try {
      const data = await getRecordings(scope, context);
      setItems(data);
    } catch {
      // Non-fatal: list just stays as-is.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const stopTimer = () => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleStop = useCallback(async () => {
    const blob = new Blob(chunksRef.current, {
      type: recorderRef.current?.mimeType || 'audio/webm',
    });
    const durationMs = accumulatedRef.current;
    chunksRef.current = [];
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    if (!blob.size) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await uploadRecording(blob, scope, context, durationMs);
      setItems((prev) => [saved, ...prev]);
      // A fresh transcript may have been appended to the entity comment server-side.
      if (saved.transcript && saved.status === 'transcribed') onTranscribed?.();
    } catch (err: any) {
      setError(err?.message || 'Failed to save recording');
    } finally {
      setBusy(false);
    }
  }, [contextKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          window.isSecureContext
            ? 'Microphone recording is not supported in this browser'
            : 'Microphone access requires a secure connection (HTTPS or localhost)'
        );
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        handleStop();
      };
      recorderRef.current = recorder;
      accumulatedRef.current = 0;
      startedAtRef.current = Date.now();
      recorder.start();
      setRecording(true);
      setPaused(false);
      setElapsed(0);
      timerRef.current = window.setInterval(() => {
        setElapsed(elapsedNow());
      }, 250);
    } catch (err: any) {
      setError(
        err?.name === 'NotAllowedError'
          ? 'Microphone permission denied'
          : err?.message || 'Could not access microphone'
      );
    }
  };

  const pause = () => {
    if (!recorderRef.current || recorderRef.current.state !== 'recording') return;
    try {
      recorderRef.current.pause();
    } catch {
      return;
    }
    stopTimer();
    accumulatedRef.current = elapsedNow();
    startedAtRef.current = 0;
    setElapsed(accumulatedRef.current);
    setPaused(true);
  };

  const resume = () => {
    if (!recorderRef.current || recorderRef.current.state !== 'paused') return;
    try {
      recorderRef.current.resume();
    } catch {
      return;
    }
    startedAtRef.current = Date.now();
    setPaused(false);
    timerRef.current = window.setInterval(() => {
      setElapsed(elapsedNow());
    }, 250);
  };

  const stop = () => {
    stopTimer();
    accumulatedRef.current = elapsedNow();
    startedAtRef.current = 0;
    setRecording(false);
    setPaused(false);
    try {
      recorderRef.current?.stop();
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    return () => {
      stopTimer();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const onRetry = async (id: string) => {
    try {
      const updated = await retryTranscription(id);
      setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
    } catch (err: any) {
      setError(err?.message || 'Retry failed');
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm('Delete this voice note? This cannot be undone.')) return;
    try {
      await deleteRecording(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      setClassified((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (err: any) {
      setError(err?.message || 'Delete failed');
    }
  };

  const startEdit = (it: VoiceRecording) => {
    setEditingId(it.id);
    setEditText(it.transcript || '');
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async (id: string) => {
    setSavingEdit(true);
    setError(null);
    try {
      const updated = await updateTranscript(id, editText);
      setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
      setEditingId(null);
      setEditText('');
    } catch (err: any) {
      setError(err?.message || 'Failed to save transcript');
    } finally {
      setSavingEdit(false);
    }
  };

  const onClassify = async (id: string) => {
    setClassifyingId(id);
    setError(null);
    try {
      const result = await classifyRecording(id);
      setClassified((prev) => ({ ...prev, [id]: result }));
    } catch (err: any) {
      setError(err?.message || 'Classify failed');
    } finally {
      setClassifyingId(null);
    }
  };

  return (
    <div className={`voice-recorder voice-recorder--${scope}`}>
      <div className="vr-head">
        <div className="vr-titles">
          <span className="vr-title">{title || 'Voice notes'}</span>
          {subtitle && <span className="vr-subtitle">{subtitle}</span>}
        </div>
        <div className="vr-controls">
          {recording && (
            <span className={`vr-timer${paused ? ' vr-timer--paused' : ''}`}>
              {paused && <span className="vr-paused-tag">Paused</span>}
              {fmtTime(elapsed)}
            </span>
          )}
          {!recording ? (
            <button
              type="button"
              className="vr-btn vr-btn--rec"
              onClick={start}
              disabled={busy}
            >
              <span className="vr-dot" /> Record
            </button>
          ) : (
            <>
              {!paused ? (
                <button type="button" className="vr-btn vr-btn--pause" onClick={pause}>
                  <span className="vr-bars" /> Pause
                </button>
              ) : (
                <button type="button" className="vr-btn vr-btn--resume" onClick={resume}>
                  <span className="vr-dot" /> Resume
                </button>
              )}
              <button type="button" className="vr-btn vr-btn--stop" onClick={stop}>
                <span className="vr-square" /> Stop
              </button>
            </>
          )}
        </div>
      </div>

      {busy && <div className="vr-status">Saving & transcribing…</div>}
      {error && <div className="vr-error">{error}</div>}

      {items.length > 0 && (
        <ul className="vr-list">
          {items.map((it) => {
            const owned = it.user_id == null || it.user_id === currentUserId;
            return (
            <li key={it.id} className={`vr-item vr-item--${it.status}`}>
              <div className="vr-item-meta">
                <span className="vr-when">{fmtWhen(it.timestamp)}</span>
                {it.duration_ms != null && (
                  <span className="vr-dur">{fmtTime(it.duration_ms)}</span>
                )}
                <span className={`vr-badge vr-badge--${it.status}`}>
                  {statusLabel[it.status] || it.status}
                </span>
                {it.approved && (
                  <span className="vr-badge vr-badge--shared" title="Approved by an admin — visible to all users">
                    {owned ? 'Shared' : `Shared by ${it.username || 'another user'}`}
                  </span>
                )}
                <span className="vr-spacer" />
                {owned && (it.status === 'awaiting_key' || it.status === 'failed') && (
                  <button className="vr-mini" onClick={() => onRetry(it.id)}>
                    Retry
                  </button>
                )}
                {owned &&
                  (it.status === 'transcribed' || it.status === 'empty') &&
                  editingId !== it.id && (
                    <button className="vr-mini" onClick={() => startEdit(it)}>
                      Edit
                    </button>
                  )}
                {owned && it.transcript && (
                  <button
                    className="vr-mini vr-mini--classify"
                    onClick={() => onClassify(it.id)}
                    disabled={classifyingId === it.id}
                  >
                    {classifyingId === it.id
                      ? 'Sorting…'
                      : classified[it.id]
                      ? 'Re-sort'
                      : 'Sort into records'}
                  </button>
                )}
                {owned && (
                  <button className="vr-mini vr-mini--del" onClick={() => onDelete(it.id)}>
                    Delete
                  </button>
                )}
              </div>
              <audio
                className="vr-audio"
                controls
                preload="metadata"
                src={getAudioUrl(it.id)}
                onLoadedMetadata={fixAudioDuration}
              />
              {editingId === it.id ? (
                <div className="vr-edit">
                  <textarea
                    className="vr-edit-area"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <div className="vr-edit-actions">
                    <button
                      className="vr-mini vr-mini--save"
                      onClick={() => saveEdit(it.id)}
                      disabled={savingEdit}
                    >
                      {savingEdit ? 'Saving…' : 'Save'}
                    </button>
                    <button className="vr-mini" onClick={cancelEdit} disabled={savingEdit}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : it.transcript ? (
                classified[it.id] ? (
                  // Once sorted into records below, the full transcript is redundant —
                  // keep it one click away instead of duplicating it on screen.
                  <div className="vr-raw">
                    <button
                      type="button"
                      className="vr-raw-toggle"
                      onClick={() =>
                        setShowRaw((prev) => ({ ...prev, [it.id]: !prev[it.id] }))
                      }
                      aria-expanded={!!showRaw[it.id]}
                    >
                      {showRaw[it.id] ? 'Hide original transcript' : 'Show original transcript'}
                    </button>
                    {showRaw[it.id] && <p className="vr-transcript">{it.transcript}</p>}
                  </div>
                ) : (
                  <p className="vr-transcript">{it.transcript}</p>
                )
              ) : it.error ? (
                <p className="vr-transcript vr-transcript--err">{it.error}</p>
              ) : null}
              {classified[it.id] && (
                <div className="vr-classified">
                  {classified[it.id].records.length === 0 ? (
                    <span className="vr-classified-empty">No records extracted.</span>
                  ) : (
                    <ul className="vr-records">
                      {classified[it.id].records.map((rec, idx) => (
                        <li key={idx} className="vr-record">
                          <span className={`vr-rtype vr-rtype--${rec.type.toLowerCase()}`}>
                            {rec.type.replace('_', ' ')}
                          </span>
                          {rec.status === 'proposed' && (
                            <span className="vr-rgate" title="Needs confirmation in Review queue">
                              proposed · gated
                            </span>
                          )}
                          <span className="vr-rtext">{rec.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {classified[it.id].records.some((r) => r.status === 'proposed') && (
                    <p className="vr-gate-note">
                      Proposed rules wait in the <strong>Review</strong> queue until you confirm them.
                    </p>
                  )}
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default VoiceRecorder;
